import {
    OCCASION_OPTIONS,
    FESTIVAL_OPTIONS,
    ROLE_OPTIONS,
    VIBE_OPTIONS,
    COLOR_FAMILY_OPTIONS,
    COLOR_SWATCH,
    FORMALITY_LEVELS,
    GARMENT_CATEGORIES,
    getAccessoryLabel,
} from '../Data/catalog';
import './discovery_panel.css';

const toggle = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

function ChipGroup({ title, hint, options, value, onChange, required, renderLead }) {
    return (
        <div className="disc-group">
            <div className="disc-group-head">
                <span className="disc-group-title">
                    {title} {required && <span className="text-red-500">*</span>}
                </span>
                <span className="disc-group-count">{value.length ? `${value.length} selected` : 'None'}</span>
            </div>
            {hint && <div className="disc-group-hint">{hint}</div>}
            <div className="disc-chips">
                {options.map((o) => {
                    const on = value.includes(o.value);
                    return (
                        <button
                            type="button"
                            key={o.value}
                            className={`disc-chip ${on ? 'is-on' : ''}`}
                            aria-pressed={on}
                            onClick={() => onChange(toggle(value, o.value))}
                        >
                            {renderLead && renderLead(o)}
                            {o.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function DiscoveryPanel({
    kind,
    occasions, setOccasions,
    festivals, setFestivals,
    roles, setRoles,
    vibe, setVibe,
    formality, setFormality,
    colorFamilies, colorAuto, setColorFamilies, onResetColors,
    pairsWith, setPairsWith, pairOptions,
    suitsCategories, setSuitsCategories,
    onSuggest, suggestNote,
}) {
    const isAccessory = kind === 'accessory';

    return (
        <div className="disc-panel">
            <div className="disc-suggest">
                <div>
                    <strong>Auto-suggest tags</strong>
                    <span>Reads the category, name, description and colour, then fills only the groups that are still empty. Review before saving.</span>
                </div>
                <button type="button" className="disc-suggest-btn" onClick={onSuggest}>Suggest tags</button>
            </div>
            {suggestNote && <div className="disc-suggest-note">{suggestNote}</div>}

            <ChipGroup
                title="Occasions"
                hint="Where the customer will wear it. Drives the occasion pages and search."
                options={OCCASION_OPTIONS}
                value={occasions}
                onChange={setOccasions}
                required={!festivals.length}
            />

            <ChipGroup
                title="Festivals"
                hint="Shown on festival collections such as Eid or Diwali."
                options={FESTIVAL_OPTIONS}
                value={festivals}
                onChange={setFestivals}
            />

            <ChipGroup
                title="Who it is for"
                hint="Separates the groom's look from the groom's friend, brother or guest."
                options={ROLE_OPTIONS}
                value={roles}
                onChange={setRoles}
            />

            <ChipGroup
                title="Style vibe"
                options={VIBE_OPTIONS}
                value={vibe}
                onChange={setVibe}
            />

            <div className="disc-group">
                <div className="disc-group-head">
                    <span className="disc-group-title">Formality</span>
                    <span className="disc-group-count">{FORMALITY_LEVELS.find((f) => f.value === formality)?.label || 'Not set'}</span>
                </div>
                <div className="disc-scale" role="radiogroup" aria-label="Formality">
                    {FORMALITY_LEVELS.map((f) => (
                        <button
                            type="button"
                            key={f.value}
                            role="radio"
                            aria-checked={formality === f.value}
                            className={`disc-scale-step ${formality >= f.value ? 'is-filled' : ''} ${formality === f.value ? 'is-on' : ''}`}
                            onClick={() => setFormality(f.value)}
                        >
                            <span className="disc-scale-num">{f.value}</span>
                            <span className="disc-scale-label">{f.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="disc-group">
                <div className="disc-group-head">
                    <span className="disc-group-title">Colour family</span>
                    <span className="disc-group-count">
                        {colorAuto ? 'Auto from colour name / fabrics' : 'Set manually'}
                        {!colorAuto && (
                            <button type="button" className="disc-link" onClick={onResetColors}>Reset to auto</button>
                        )}
                    </span>
                </div>
                <div className="disc-group-hint">Used when a customer searches "maroon kurta" or filters by colour. Pick the dominant colour first.</div>
                <div className="disc-chips">
                    {COLOR_FAMILY_OPTIONS.map((o) => {
                        const on = colorFamilies.includes(o.value);
                        return (
                            <button
                                type="button"
                                key={o.value}
                                className={`disc-chip ${on ? 'is-on' : ''}`}
                                aria-pressed={on}
                                onClick={() => setColorFamilies(toggle(colorFamilies, o.value))}
                            >
                                <span className="disc-swatch" style={{ background: COLOR_SWATCH[o.value] }} />
                                {o.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {isAccessory ? (
                <ChipGroup
                    title="Goes with"
                    hint="Garments this accessory is suggested with on product pages."
                    options={GARMENT_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
                    value={suitsCategories}
                    onChange={setSuitsCategories}
                />
            ) : (
                <div className="disc-group">
                    <div className="disc-group-head">
                        <span className="disc-group-title">Pairs with accessories</span>
                        <span className="disc-group-count">{pairsWith.length ? `${pairsWith.length} linked` : 'None'}</span>
                    </div>
                    <div className="disc-group-hint">Shown as "Complete the look" on this product page.</div>
                    {pairOptions.length === 0 ? (
                        <div className="disc-empty">No accessories uploaded yet. Add one with the Accessory option above and it will appear here.</div>
                    ) : (
                        <div className="disc-pair-grid">
                            {pairOptions.map((a) => {
                                const on = pairsWith.includes(a.id);
                                return (
                                    <button
                                        type="button"
                                        key={a.id}
                                        className={`disc-pair ${on ? 'is-on' : ''} ${a.recommended ? 'is-recommended' : ''}`}
                                        aria-pressed={on}
                                        onClick={() => setPairsWith(toggle(pairsWith, a.id))}
                                    >
                                        {a.src ? <img src={a.src} alt="" /> : <span className="disc-pair-ph">{(a.name || '?')[0]}</span>}
                                        <span className="disc-pair-copy">
                                            <strong>{a.name}</strong>
                                            <small>{getAccessoryLabel(a.accessoryType)}{a.recommended ? ' · Suggested' : ''}</small>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
